import { stripHtml } from '@/utils/htmlText'
import type { GetContentBundleResponse, ApiSearchResult } from '@/types/api'
import { AdditionalReadingCategory } from '@/types/api'

export interface ClientSearchFilters {
  modelViewId?: number
  levelId?: number
  contentTagId?: number
}

export function clientSearch(
  bundle: GetContentBundleResponse,
  query: string,
  maxResults = 5,
  filters?: ClientSearchFilters
): ApiSearchResult[] {
  const q = query.toLowerCase().trim()
  if (q.length < 2) return []

  // Pre-compute filter sets once
  const filteredPaIds: Set<number> | null = filters?.modelViewId != null
    ? new Set(bundle.modelViewPracticeAreas
        .filter(x => x.modelViewId === filters.modelViewId)
        .map(x => x.practiceAreaId))
    : null

  const selectedLevel = filters?.levelId != null
    ? bundle.levels.find(l => l.id === filters.levelId)
    : null
  const maxLevelSeq = selectedLevel?.sequence ?? null

  const practiceIdsAtLevel: Set<number> | null = maxLevelSeq !== null
    ? new Set(bundle.practiceLevels
        .filter(pl => {
          const seq = bundle.levels.find(l => l.id === pl.levelId)?.sequence ?? 0
          return seq <= maxLevelSeq
        })
        .map(pl => pl.practiceId))
    : null

  const results: ApiSearchResult[] = []

  // Search practice areas
  for (const pa of bundle.practiceAreas) {
    if (results.length >= maxResults) break
    if (filteredPaIds && !filteredPaIds.has(pa.id)) continue
    const text = [
      pa.displayName, pa.description, pa.intent, pa.value,
      pa.additionalExplanatoryInformation, pa.additionalRequiredInformation,
    ].map(f => f ?? '').join(' ').toLowerCase()
    if (text.includes(q)) {
      results.push({
        contentType: 'PracticeArea',
        entityId: pa.id,
        title: pa.displayName,
        snippet: getBestSnippet(
          [pa.displayName, pa.description, pa.intent, pa.value,
           pa.additionalExplanatoryInformation, pa.additionalRequiredInformation],
          query,
        ),
        practiceAreaId: pa.id,
        practiceAreaName: pa.displayName,
        parentPracticeId: null,
        relevanceScore: pa.displayName.toLowerCase().includes(q) ? 2 : 1,
      })
    }
  }

  // Search practices
  for (const p of bundle.practices) {
    if (results.length >= maxResults) break
    if (filteredPaIds && !filteredPaIds.has(p.practiceAreaId)) continue
    if (practiceIdsAtLevel && !practiceIdsAtLevel.has(p.id)) continue
    const text = [
      p.displayName, p.description, p.statement, p.value,
      p.additionalExplanatoryInformation, p.additionalRequiredInformation,
      p.exampleActivities, p.exampleWorkProducts
    ].map(f => f ?? '').join(' ').toLowerCase()
    if (text.includes(q)) {
      const paName = bundle.practiceAreas.find(pa => pa.id === p.practiceAreaId)?.displayName ?? null
      results.push({
        contentType: 'Practice',
        entityId: p.id,
        title: p.displayName,
        snippet: getBestSnippet(
          [p.displayName, p.description, p.statement, p.value,
           p.additionalExplanatoryInformation, p.additionalRequiredInformation,
           p.exampleActivities, p.exampleWorkProducts],
          query,
        ),
        practiceAreaId: p.practiceAreaId,
        practiceAreaName: paName,
        parentPracticeId: null,
        relevanceScore: p.displayName.toLowerCase().includes(q) ? 2 : 1,
      })
    }
  }

  // Search additional reading content
  for (const arc of bundle.additionalReading) {
    if (results.length >= maxResults) break
    const text = [arc.displayName, arc.content]
      .map(f => f ?? '')
      .join(' ')
      .toLowerCase()
    if (text.includes(q)) {
      const contentType: ApiSearchResult['contentType'] =
        arc.category === AdditionalReadingCategory.Overview ? 'Overview' :
        arc.category === AdditionalReadingCategory.Appendices ? 'Appendices' :
        arc.category === AdditionalReadingCategory.ReleaseNotes ? 'ReleaseNotes' :
        arc.category === AdditionalReadingCategory.Crosswalks ? 'Crosswalks' :
        'Faq'

      results.push({
        contentType,
        entityId: arc.id,
        title: arc.displayName,
        snippet: getBestSnippet([arc.displayName, arc.content], query),
        practiceAreaId: null,
        practiceAreaName: null,
        parentPracticeId: null,
        relevanceScore: arc.displayName.toLowerCase().includes(q) ? 2 : 1,
      })
    }
  }

  // Search contextual content
  for (const cc of bundle.contextualContent) {
    if (results.length >= maxResults) break
    if (filters?.contentTagId != null && cc.contentTagId !== filters.contentTagId) continue
    const parentPaId = cc.practiceAreaId
      ?? bundle.practices.find(p => p.id === cc.practiceId)?.practiceAreaId
      ?? null
    if (filteredPaIds && parentPaId !== null && !filteredPaIds.has(parentPaId)) continue
    if (practiceIdsAtLevel && cc.practiceId !== null && !practiceIdsAtLevel.has(cc.practiceId)) continue
    const text = (cc.content ?? '').replace(/<[^>]+>/g, '').toLowerCase()
    if (text.includes(q)) {
      const tag = bundle.contentTags.find(t => t.id === cc.contentTagId)
      const tagName = tag?.displayName ?? tag?.name ?? 'Context'
      const parentPractice = cc.practiceId != null
        ? bundle.practices.find(p => p.id === cc.practiceId)
        : null
      const parentPa = parentPaId != null
        ? bundle.practiceAreas.find(pa => pa.id === parentPaId)
        : null
      const parentName = parentPractice?.displayName ?? parentPa?.displayName ?? null
      const title = parentName ? `${tagName} — ${parentName}` : tagName
      results.push({
        contentType: 'ContextualContent',
        entityId: cc.id,
        title,
        snippet: getBestSnippet([cc.content], query),
        practiceAreaId: parentPaId,
        practiceAreaName: null,
        parentPracticeId: cc.practiceId ?? null,
        relevanceScore: 1,
      })
    }
  }

  // Search external frameworks (name / short code / description) — navigate to the framework page
  const frameworks = bundle.externalFrameworks ?? []
  for (const fw of frameworks) {
    if (results.length >= maxResults) break
    const description = stripHtml(fw.description)
    const text = [fw.name, fw.shortCode, description].join(' ').toLowerCase()
    if (text.includes(q)) {
      results.push({
        contentType: 'Framework',
        entityId: fw.id,
        title: fw.name,
        snippet: getBestSnippet([description || null, fw.name], query),
        practiceAreaId: null,
        practiceAreaName: null,
        parentPracticeId: null,
        relevanceScore: fw.name.toLowerCase().includes(q) ? 2 : 1,
      })
    }
  }

  // Search external framework clauses (clause key / topic) — navigate to the owning framework page
  const frameworkById = new Map(frameworks.map(fw => [fw.id, fw]))
  for (const clause of bundle.externalFrameworkClauses ?? []) {
    if (results.length >= maxResults) break
    const text = [clause.clauseKey, clause.topic].map(f => f ?? '').join(' ').toLowerCase()
    if (text.includes(q)) {
      const fw = frameworkById.get(clause.externalFrameworkId)
      if (!fw) continue
      results.push({
        contentType: 'Framework',
        entityId: fw.id,
        title: `${clause.clauseKey} — ${clause.topic}`,
        snippet: fw.name,
        practiceAreaId: null,
        practiceAreaName: null,
        parentPracticeId: null,
        relevanceScore: clause.clauseKey.toLowerCase().includes(q) ? 2 : 1,
      })
    }
  }

  const typeGroup = (r: ApiSearchResult) =>
    r.contentType === 'PracticeArea' || r.contentType === 'Practice' ? 0 : 1

  return results
    .sort((a, b) => {
      const groupDiff = typeGroup(a) - typeGroup(b)
      if (groupDiff !== 0) return groupDiff
      const scoreDiff = b.relevanceScore - a.relevanceScore
      if (scoreDiff !== 0) return scoreDiff
      const paA = (a.practiceAreaName ?? a.title).toLowerCase()
      const paB = (b.practiceAreaName ?? b.title).toLowerCase()
      return paA < paB ? -1 : paA > paB ? 1 : 0
    })
    .slice(0, maxResults)
}

function getBestSnippet(fields: (string | null)[], query: string): string {
  const q = query.toLowerCase()
  for (const field of fields) {
    if (field && field.toLowerCase().includes(q)) {
      return getSnippet(field, query)
    }
  }
  return getSnippet(fields.find(f => f != null) ?? '', query)
}

function getSnippet(text: string, query: string, context = 80): string {
  const plainText = text.replace(/<[^>]+>/g, '')
  const idx = plainText.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return plainText.slice(0, context * 2)
  const start = Math.max(0, idx - context)
  const end = Math.min(plainText.length, idx + query.length + context)
  return (start > 0 ? '...' : '') + plainText.slice(start, end) + (end < plainText.length ? '...' : '')
}

export function highlightSearchTerm(text: string, query: string): string {
  if (!query) return text
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>')
}
